"use client";
import { TypeCta } from "@contentful/types";
import {
  faArrowUpRightFromSquare,
  faFileArrowDown,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@yardzen/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@yardzen/ui/components/dialog";
import { H2 } from "@yardzen/ui/components/h2";
import { Spinner } from "@yardzen/ui/components/spinner";
import { Asset } from "contentful";
import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

export function PDFModal({
  contentfulCTA,
  pdfContent,
  modalCta,
}: {
  contentfulCTA: TypeCta<"WITHOUT_UNRESOLVABLE_LINKS">;
  pdfContent: Asset<"WITHOUT_UNRESOLVABLE_LINKS">;
  modalCta: TypeCta<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();

  const { title, description } = modalCta.fields;

  const [docArray, setDocArray] = useState<number[]>([]);
  const [windowWidth, setWindowWidth] = useState(300);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    const docArray = Array.from({ length: numPages }, (_, index) => index + 1);
    setDocArray(docArray);
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth - 32);
    };

    // Set initial values on mount
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          className="w-full px-4 py-3 md:w-auto"
          variant={contentfulCTA.fields.variant}
        >
          {contentfulCTA.fields.buttonText}
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-brand-moss-greens-10 h-full max-h-full w-full p-4  sm:p-8 md:max-h-[90%] md:max-w-[90%] lg:h-full">
        <VisuallyHidden asChild>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>
        <div className="flex h-full w-full flex-col  items-center justify-center">
          <div className="flex w-full flex-col items-center justify-center pb-8">
            <H2 className="text-brand-primary-darker">{title}</H2>
            <p className="text-brand-primary-darker">{description}</p>
          </div>
          <div
            id="pdf-container-desktop"
            className="hidden h-2/3 w-full sm:block"
          >
            <embed
              type="application/pdf"
              src={pdfContent.fields.file?.url}
              className="h-full w-full"
            />
          </div>
          <div
            id="pdf-container"
            className={`h-[350px] w-full overflow-y-auto sm:hidden sm:h-full`}
          >
            <Document
              file={pdfContent.fields.file?.url}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<Spinner />}
            >
              {docArray.map((pageNumber) => (
                <Page
                  key={pageNumber}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  width={windowWidth}
                />
              ))}
            </Document>
          </div>
          <div className="flex flex-col gap-4 pt-8 sm:flex-row">
            <Button
              variant="default"
              startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
              onClick={async () => {
                //all of this is to avoid CORS issues for downloading the PDF
                const url = pdfContent.fields.file?.url;
                if (!url) return;
                try {
                  const response = await fetch(url, { mode: "cors" });
                  const blob = await response.blob();
                  const blobUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  link.download = pdfContent.fields.title || "download.pdf";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(blobUrl);
                } catch (e) {
                  // Optionally handle error
                  window.open(url, "_blank");
                }
              }}
            >
              Download PDF
            </Button>
            <Button
              variant="outline"
              startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
              onClick={() => {
                window.open(pdfContent.fields.file?.url, "_blank");
              }}
            >
              Open In New Tab
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
